import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormControlStatus,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AssistOrderItem,
  ConfigService,
  OrdersService,
  ReservationWithHint,
  ServiceSelectDataItem,
} from '@assist/shared/data';
import { translation } from '@cue/translations';
import { LoadingComponent } from '@cue/loading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { catchError, forkJoin, map, of, Subject, takeUntil } from 'rxjs';
import { OrderItemDescriptor } from '@cue/api';
import { cueAdd, cueClose, IconComponent } from '@cue/ui/icons';
import { LocalizedStringPipe, RtlDirective } from '@cue/utilities';
import { localized } from '@cue/translate';
import { rxResource } from '@angular/core/rxjs-interop';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { tap } from 'rxjs/operators';
import { ServiceSelectComponent } from '../service-select/service-select.component';
import { DateFormatPipe, TimeFormatPipe } from '@cue/pipes';
import { ResourceTitlePipe } from '@assist/ui/pipes';
import { NgStyle } from '@angular/common';
import { ExpansionPanelComponent, ExpansionPanelTitleDirective } from '@progress/kendo-angular-layout';

type OneResourceFormGroup = FormGroup<{
  resourceIdForFiltering: FormControl<number>;
  items: FormArray<any>;
  note: FormControl<string>;
}>;

type AllResourcesFormArray = FormArray<OneResourceFormGroup>;

@Component({
  selector: 'assist-reservation-detail-orders-tab',
  templateUrl: './reservation-detail-orders-tab.component.html',
  styleUrls: ['./reservation-detail-orders-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DateFormatPipe,
    TimeFormatPipe,
    ServiceSelectComponent,
    TranslocoPipe,
    IconComponent,
    LoadingComponent,
    LocalizedStringPipe,
    ResourceTitlePipe,
    RtlDirective,
    NgStyle,
    ExpansionPanelComponent,
    ExpansionPanelTitleDirective,
  ],
})
export class ReservationDetailOrdersTabComponent implements OnInit {
  reservation = input<ReservationWithHint>();

  createOrdersFormForResource(resourceId: number) {
    return this.fb.group({
      resourceIdForFiltering: new FormControl(resourceId, { nonNullable: true }),
      items: new FormArray([]),
      note: new FormControl<string>('', { nonNullable: true }),
    }) as OneResourceFormGroup;
  }

  resourcesOrdersArray = computed(() => {
    if (!this.reservation()) {
      return new FormArray([]) as AllResourcesFormArray;
    }

    const arr: AllResourcesFormArray = new FormArray([]);
    this.reservation().resourceAttendees.forEach((rA) => {
      const group = this.createOrdersFormForResource(rA.resource.idForFiltering);
      arr.push(group);
    });
    return arr;
  });

  _subSwitch = new Subject<any>();
  onArrayChange = explicitEffect([this.resourcesOrdersArray], ([formArray]) => {
    this._subSwitch.next(formArray);
    formArray.valueChanges
      .pipe(
        takeUntil(this._subSwitch),
        tap((value) => this.ordersChanged.emit(value as any))
      )
      .subscribe();

    formArray.statusChanges
      .pipe(
        takeUntil(this._subSwitch),
        tap((value) => this.ordersValidityChanged.emit(value))
      )
      .subscribe();
  });

  ordersChanged = output<{ resourceIdForFiltering: number; items: any[]; note: string }[]>();
  ordersValidityChanged = output<FormControlStatus>();

  data = computed(() => {
    const resAtts = this.reservation()?.resourceAttendees ?? [];
    const allowedOrderServicesByResourceId = this.allowedOrderServicesByResource.value() ?? new Map<number, number[]>();
    const orderServicesLoading = this.allowedOrderServicesByResource.isLoading();

    return resAtts.map((rA, rAindex) => {
      const group = this.resourcesOrdersArray().controls.find(
        (x) => x.value.resourceIdForFiltering === rA.resource.idForFiltering
      );
      const previousOrders = this.previousOrders
        .value()
        ?.filter((x) => x.resourceIdForFiltering === rA.resource.idForFiltering);
      const allowedServiceIds = allowedOrderServicesByResourceId.get(rA.resource.idForFiltering) ?? [];
      const adding = this.adding()[rAindex] === true ? true : false;
      return {
        adding: adding,
        canAddOrders: this.configService.value.enableOrders && allowedServiceIds.length > 0,
        formGroup: group,
        orderServicesLoading,
        resourceAttendee: rA,
        previousOrders: previousOrders,
      };
    });
  });

  configService = inject(ConfigService);
  translocoService = inject(TranslocoService);
  ordersService = inject(OrdersService);
  fb = inject(FormBuilder);

  translation = translation;

  ICONS = {
    cueClose,
    cueAdd,
  };

  adding = signal<boolean[]>([]);
  // pro kazdy location
  usedServiceIds = signal<Record<number, boolean>[]>([]);

  setAdding(index: number, newValue: boolean) {
    this.adding.update((s) => {
      const newArr = [...s];
      newArr[index] = newValue;
      return newArr;
    });
  }

  currentOrders = input<
    {
      resourceIdForFiltering: number;
      note: string;
      items: any[];
    }[]
  >();
  mode = input<'detail' | 'new'>('detail');
  state = input<'view' | 'edit' | 'loading'>('view');

  previousOrders = rxResource({
    stream: () =>
      this.reservation()
        ? this.ordersService.getEventOrders({
            iCalUid: this.reservation().iCalUId,
            id: this.reservation().id,
          })
        : of([]),
  });

  private readonly allowedOrderServicesParams = computed(() => {
    if (!this.configService.value.enableOrders) {
      return undefined;
    }

    const resourceAttendees = this.reservation()?.resourceAttendees ?? [];

    if (resourceAttendees.length === 0) {
      return undefined;
    }

    return {
      key: resourceAttendees.map((resourceAttendee) => resourceAttendee.resource.idForFiltering).join(','),
      resourceAttendees,
    };
  });
  private readonly allowedOrderServicesByResource = rxResource({
    params: () => this.allowedOrderServicesParams(),
    stream: ({ params }) =>
      forkJoin(
        params.resourceAttendees.map((resourceAttendee) =>
          this.ordersService.getAllowedServiceIdsForResourceGuid(resourceAttendee.resource.id).pipe(
            catchError(() => of([])),
            map((serviceIds) => [resourceAttendee.resource.idForFiltering, serviceIds] satisfies [number, number[]])
          )
        )
      ).pipe(map((entries) => new Map<number, number[]>(entries))),
  });

  eventDate = input<Date>();

  ngOnInit(): void {
    this.resourcesOrdersArray().controls.forEach((oneResourceGroup) => {
      const currentOrders = this.currentOrders().find(
        (x) => x.resourceIdForFiltering === oneResourceGroup.value.resourceIdForFiltering
      );
      if (currentOrders) {
        oneResourceGroup.get('note').setValue(currentOrders.note);
        currentOrders.items.forEach((item) => {
          const serviceInfo = this.getServiceInfo(item);
          if (serviceInfo) {
            const group = this.createNewOrderItem(serviceInfo as any, false);
            group.patchValue(item);
            oneResourceGroup.controls.items.push(group);
          }
        });
      }
    });
  }

  getServiceInfo(item: AssistOrderItem | OrderItemDescriptor) {
    return this.configService.value.services.find((x) => x.id === item.serviceId);
  }

  onSelect(index: number, resourceIdForFiltering: number, item: ServiceSelectDataItem) {
    this.usedServiceIds.update((s) => {
      return s.map((x, xI) => {
        return xI === index
          ? {
              ...x,
              [item.id]: true,
            }
          : x;
      });
    });

    const group = this.resourcesOrdersArray().controls.find(
      (x) => x.value.resourceIdForFiltering === resourceIdForFiltering
    );
    group.controls.items.push(this.createNewOrderItem(item));
  }

  remove(resourceIdForFiltering: number, order: AbstractControl, index: number) {
    this.usedServiceIds.update((old) => {
      return old.map((oldArr, i) => {
        if (index === i) {
          return {
            ...oldArr,
            [order.value.serviceId]: undefined,
          };
        }
        return oldArr;
      });
    });
    // this.usedServiceIds.update((s) => ({
    //   ...s,
    //   [order.value.serviceId]: undefined,
    // }));
    const group = this.resourcesOrdersArray().controls.find(
      (x) => x.value.resourceIdForFiltering === resourceIdForFiltering
    );
    group.controls.items.removeAt(index);
  }

  createNewOrderItem(item: ServiceSelectDataItem, emitEvent = true) {
    const group: FormGroup<{
      serviceId: FormControl<number>;
      name: FormControl<string>;
      imageUrl: FormControl<string>;
      noteAllowed: FormControl<boolean>;
      note?: FormControl<string>;
      noteMinLength?: FormControl<number>;
    }> = this.fb.group({
      serviceId: new FormControl(item.id, { nonNullable: true }),
      name: new FormControl(localized(item.name, this.translocoService), { nonNullable: true }),
      imageUrl: new FormControl(item.imageUrl, { nonNullable: true }),
      noteAllowed: new FormControl(item.noteAllowed, { nonNullable: true }),
    });

    if (item.noteAllowed) {
      group.addControl('note', new FormControl('', { nonNullable: true }), {
        emitEvent: emitEvent,
      });
      if (item.noteMinLength > 0) {
        group.get('note').addValidators([Validators.required, Validators.minLength(item.noteMinLength)]);

        group.get('note').updateValueAndValidity({ emitEvent: emitEvent });
      }
      group.addControl('noteMinLength', new FormControl(item.noteMinLength, { nonNullable: true }), {
        emitEvent: emitEvent,
      });
    }
    return group;
  }
}
